import React, { useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";
import { Box, Flex, Grid } from "theme-ui";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectDate from "components/forms/SelectDate";
import SelectSearch from "components/forms/SelectSearch";
import Checkbox from "components/forms/Checkbox";
import Subtext from "components/typography/Subtext";
import { CheckMarkIcon } from "components/svg";
import { useToast } from "components/toast";

import { createServicePlan } from "api/ServicePlans";
import { getClients } from "api/Clients";
import {
  NotificationProfile,
  PROFILE_DESCRIPTIONS,
  profileMatrix,
  compactMatrix,
} from "./notificationProfiles";
import { themevals } from "theme/themevals";
import { mnt } from "theme/maintenanceTokens";

/**
 * MASTER_PLAN v3.4 — shared Create Maintenance Plan modal.
 *
 * Two entry points, one modal:
 *   1. /maintenance header primary action → opens without `clientId`,
 *      renders a client picker as the first field.
 *   2. (Historically) a client-context caller could pass `clientId` in
 *      and the picker would be hidden. Post-v3.4 correction, the
 *      client-detail Plans tab no longer exposes a create action —
 *      but the prop shape stays flexible so a future callsite (e.g.
 *      "New Maintenance Plan" from a client's Overview quick-action)
 *      can pre-select the client without needing a second modal.
 *
 * Backend field is still `servicePlan.clientId` (legacy code identifier
 * per v3.4 preamble). Every user-visible string is "Maintenance Plan".
 */
export default function CreateMaintenancePlanModal({
  clientId,
  onClose,
  onCreated,
}: {
  clientId?: string;
  onClose: () => void;
  onCreated: (plan: any) => void;
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  const clientPreselected = !!clientId;

  const _validate = Yup.object().shape({
    clientId: Yup.string().required("Client is required."),
    name: Yup.string().required("Plan name is required."),
    isTermBased: Yup.boolean(),
    endDate: Yup.number().when("isTermBased", {
      is: true,
      then: (schema) => schema.required("End date is required for a term-based plan."),
      otherwise: (schema) => schema.nullable(),
    }),
    committedSiteCount: Yup.number().nullable().min(0, "Must be zero or more."),
  });

  const submitCreate = async (values: any) => {
    setSubmitting(true);

    // GAP-10 — a plan is born with a deliberate notification posture.
    // corrigo_managed is all-silent: store an empty object {} (deliberate
    // silence the gate can read), NOT null (which reads as unconfigured).
    // self_service / mixed store their compacted true-only matrix.
    const chosenProfile: NotificationProfile = values.notificationProfile || "self_service";
    const notificationConfig = compactMatrix(profileMatrix(chosenProfile));

    const res = await createServicePlan({
      data: {
        clientId: values.clientId,
        name: values.name,
        intakeStyle: values.intakeStyle === "work_order_fed" ? "work_order_fed" : "quote_fed",
        endDate:
          values.isTermBased && values.endDate
            ? moment.unix(values.endDate).format("YYYY-MM-DD")
            : null,
        committedSiteCount:
          values.isTermBased && values.committedSiteCount
            ? Number(values.committedSiteCount)
            : null,
        notes: values.notes || null,
        // Always sent — {} for the all-silent corrigo_managed posture so
        // the plan is never born "unconfigured".
        notificationConfig,
      },
    });

    setSubmitting(false);

    if (res?.id) {
      toast.add("Maintenance Plan created.", "var(--theme-ui-colors-success)");
      onCreated(res);
    } else {
      toast.add(
        res?.message ? res.message : "Error creating Maintenance Plan.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>New Maintenance Plan</ModalHeader>
      <Formik
        initialValues={{
          clientId: clientId || "",
          name: "",
          intakeStyle: "quote_fed",
          isTermBased: false,
          endDate: undefined,
          committedSiteCount: "",
          notes: "",
          notificationProfile: "self_service",
        }}
        validationSchema={_validate}
        onSubmit={submitCreate}
      >
        {({ values, errors, submitCount, setFieldValue }) => (
          <Form noValidate autoComplete="off">
            <ModalBody>
              {!clientPreselected && (
                <Box sx={{ marginBottom: "20px" }}>
                  <SelectSearch
                    name="clientId"
                    label="Client"
                    required
                    api={getClients}
                    value={values.clientId}
                    keyValue="id"
                    keyLabel={["name"]}
                    sortBy="name"
                    sortDir="ASC"
                    preload
                    onChange={(client: any) => setFieldValue("clientId", client?.id || "")}
                    $errors={errors.clientId && submitCount > 0 ? errors.clientId : null}
                    placeholder="Search clients..."
                  />
                </Box>
              )}
              <TextInput
                name="name"
                label="Plan Name"
                required
                value={values.name}
                onChange={(val: any) => setFieldValue("name", val)}
                $errors={errors.name && submitCount > 0 ? errors.name : null}
                placeholder="e.g. EMCOR / LCG · FY27"
              />
              <Box sx={{ marginTop: "20px" }}>
                <Flex sx={{ gap: "1.5rem" }}>
                  <Checkbox
                    label="Quote-fed"
                    checked={values.intakeStyle === "quote_fed"}
                    onChange={() => setFieldValue("intakeStyle", "quote_fed")}
                    labelSize="14px"
                  />
                  <Checkbox
                    label="Work-order-fed"
                    checked={values.intakeStyle === "work_order_fed"}
                    onChange={() => setFieldValue("intakeStyle", "work_order_fed")}
                    labelSize="14px"
                  />
                </Flex>
                <Subtext marginTop={"5px"} marginBottom={0} fontSize={"12px"} fontFamily={"Poppins"}>
                  Quote-fed: you issue a quote directly at a location and the client accepts it (church, HOA, most clients). Work-order-fed: the client sends work orders you convert (EMCOR / Corrigo). This decides which tools the plan shows.
                </Subtext>
              </Box>
              <Box sx={{ marginTop: "20px" }}>
                <Subtext
                  fontSize={"12px"}
                  fontFamily={"Poppins"}
                  margin={0}
                  marginBottom={"6px"}
                  color={themevals.colors.dkgrey}
                >
                  Notifications
                </Subtext>
                {/* GAP-10 — pick a notification posture at create so the
                 * plan is born deliberate, not unconfigured. Custom
                 * tuning (the full event x recipient matrix) stays in
                 * Edit; create offers the three presets only. */}
                <Flex sx={{ gap: "8px", flexWrap: "wrap" }}>
                  {(["corrigo_managed", "self_service", "mixed"] as NotificationProfile[]).map((p) => {
                    const active = values.notificationProfile === p;
                    const labels: Record<string, string> = {
                      corrigo_managed: "Corrigo-managed",
                      self_service: "Self-service",
                      mixed: "Mixed",
                    };
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setFieldValue("notificationProfile", p)}
                        style={{
                          background: active ? mnt.ink : mnt.cardBg,
                          color: active ? mnt.cardBg : mnt.ink,
                          border: active
                            ? "1px solid " + mnt.ink
                            : "1px solid " + mnt.divider,
                          borderRadius: "4px",
                          padding: "8px 14px",
                          fontSize: "12px",
                          fontFamily: "Poppins",
                          cursor: "pointer",
                        }}
                      >
                        {labels[p]}
                      </button>
                    );
                  })}
                </Flex>
                <Subtext marginTop={"6px"} marginBottom={0} fontSize={"11px"} fontFamily={"Poppins"} color={themevals.colors.dkgrey}>
                  {PROFILE_DESCRIPTIONS[values.notificationProfile as NotificationProfile]} You can fine-tune every event after creating the plan.
                </Subtext>
              </Box>
              <Box sx={{ marginTop: "20px" }}>
                <Flex sx={{ gap: "1.5rem" }}>
                  <Checkbox
                    label="Ongoing"
                    checked={!values.isTermBased}
                    onChange={() => {
                      setFieldValue("isTermBased", false);
                      setFieldValue("endDate", undefined);
                      setFieldValue("committedSiteCount", "");
                    }}
                    labelSize="14px"
                  />
                  <Checkbox
                    label="Term-based"
                    checked={values.isTermBased}
                    onChange={() => setFieldValue("isTermBased", true)}
                    labelSize="14px"
                  />
                </Flex>
                <Subtext marginTop={"5px"} marginBottom={0} fontSize={"12px"} fontFamily={"Poppins"}>
                  Ongoing plans show a cycle count and next-due date. Term-based plans show a countdown and progress bar against a committed site count.
                </Subtext>
              </Box>
              {values.isTermBased && (
                <Grid gap={3} columns={[1, 2]} sx={{ marginTop: "20px" }}>
                  <SelectDate
                    name="endDate"
                    label="Target End Date"
                    value={values.endDate}
                    onChange={(val: any) => setFieldValue("endDate", val)}
                    $errors={errors.endDate && submitCount > 0 ? errors.endDate : null}
                  />
                  <TextInput
                    type="number"
                    name="committedSiteCount"
                    label="Committed Site Count"
                    min={0}
                    value={values.committedSiteCount}
                    onChange={(val: any) => setFieldValue("committedSiteCount", val)}
                    $errors={errors.committedSiteCount && submitCount > 0 ? errors.committedSiteCount : null}
                  />
                </Grid>
              )}
              <Box sx={{ marginTop: "20px" }}>
                <TextInput
                  type="textarea"
                  name="notes"
                  label="Notes"
                  rows={3}
                  value={values.notes}
                  onChange={(val: any) => setFieldValue("notes", val)}
                  placeholder="Optional context for other staff."
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <BasicButton type="button" outline scheme="secondary" onClick={onClose}>
                Cancel
              </BasicButton>
              <BasicButton
                type="submit"
                $submitting={submitting}
                $iconHeight="14px"
                $iconWidth="14px"
                $loadingColor="#fff"
                keepText={true}
              >
                {!submitting && <CheckMarkIcon fill={"#fff"} />}
                Create Plan
              </BasicButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
